import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ago } from '../../components/three/ecosystemState';
import { apiGet } from '../../utils/backendApi';
import { LiveSection } from './LiveFrame';
import { appLive } from './routes';

// Operator posture board — defense in depth, one section per layer, each
// read from what the plane already measures (brain: respond-to-a-runtime-
// advisory, fleet-ops). Nothing here runs a scan; the Scans tab and the
// per-app Security page keep the buttons.

const SEV = ['critical', 'high', 'medium', 'low'];
const SHORT = (d) => (d ? String(d).replace(/^sha256:/, '').slice(0, 12) : '—');
const imageShort = (ref) => (ref ? String(ref).replace(/^.*\//, '').replace(/@sha256:(\w{12}).*$/, '@$1') : '—');
const n = (x) => Number(x) || 0;

function Dot({ state }) {
  return <i className={`live-dot is-${state}`} aria-hidden="true" />;
}

function SevCells({ counts, keys = SEV }) {
  return keys.map((k) => <td key={k} className={`live-num live-sev is-${k} ${n(counts?.[k]) ? '' : 'is-zero'}`}>{n(counts?.[k])}</td>);
}

// Each layer reduces to one verdict: ok / warn / down / unknown.
function layerVerdicts(d) {
  const r = d.runtime;
  const runtimeState = !r?.floor ? 'unknown'
    : (r.plane.behind === true || r.hosts.some((h) => h.behind === true) || r.kits.behind.length) ? 'down'
      : (r.kits.unreported.length || r.hosts.some((h) => h.behind == null)) ? 'warn' : 'ok';
  const bi = d.baseImage;
  const baseState = !bi?.digest ? 'unknown' : (bi.pendingKitRelease || bi.pendingPlaneBuild) ? 'warn' : 'ok';
  const imgCrit = d.images.reduce((t, i) => t + n(i.counts?.critical), 0);
  const imgHigh = d.images.reduce((t, i) => t + n(i.counts?.high), 0);
  const imgRead = d.images.filter((i) => i.counts).length;
  const imageState = !imgRead ? 'unknown' : imgCrit ? 'down' : imgHigh ? 'warn' : 'ok';
  const dastRows = d.dast.instances;
  const dastHot = dastRows.filter((x) => n(x.counts?.critical) + n(x.counts?.high) > 0).length;
  const dastScanned = dastRows.filter((x) => x.lastScanAt).length;
  const dastState = !dastScanned ? 'unknown' : dastHot ? 'warn' : 'ok';
  const repos = Object.values(d.supplyChain?.repos || {});
  const ciStates = repos.map((x) => x.ci?.status);
  const ciState = !repos.length ? 'unknown' : ciStates.some((s) => s === 'failure' || s === 'cancelled' || s === 'timed_out') ? 'down' : ciStates.some((s) => s === 'pending' || s === 'unknown' || s === 'none') ? 'warn' : 'ok';
  const alerts = repos.map((x) => x.alerts);
  const alertCrit = alerts.reduce((t, a) => t + n(a?.counts?.critical), 0);
  const alertHigh = alerts.reduce((t, a) => t + n(a?.counts?.high), 0);
  const alertOpen = alerts.reduce((t, a) => t + n(a?.open), 0);
  const alertState = !alerts.length || alerts.every((a) => a?.error) ? 'unknown' : alertCrit ? 'down' : alertHigh ? 'warn' : 'ok';
  return {
    runtime: { state: runtimeState, value: r?.floor ? `${r.kits.atFloor + r.hosts.filter((h) => h.behind === false).length + (r.plane.behind === false ? 1 : 0)} at floor` : 'no floor', detail: r?.floor ? `floor ${r.floor}` : 'set apps.monitor.minNodeVersion' },
    base: { state: baseState, value: bi?.digest ? SHORT(bi.digest) : 'unresolved', detail: bi?.changedAt ? `moved ${ago(bi.changedAt)}` : (bi?.checkedAt ? `checked ${ago(bi.checkedAt)}` : 'never checked') },
    image: { state: imageState, value: imgRead ? `${imgCrit} critical · ${imgHigh} high` : 'no scan read', detail: `${d.images.length} image${d.images.length === 1 ? '' : 's'} in service` },
    dast: { state: dastState, value: dastScanned ? `${dastHot} app${dastHot === 1 ? '' : 's'} to act on` : 'no baseline yet', detail: `${dastScanned}/${dastRows.length} scanned` },
    ci: { state: ciState, value: repos.length ? repos.map((x) => `${x.repo.split('/')[1]} ${x.ci?.status || '?'}`).join(' · ') : 'not watched', detail: d.supplyChain?.checkedAt ? `read ${ago(d.supplyChain.checkedAt)}` : 'set apps.planeRepo' },
    deps: { state: alertState, value: alertState === 'unknown' ? 'alerts unreadable' : `${alertOpen} open alert${alertOpen === 1 ? '' : 's'}`, detail: alertState === 'unknown' ? (alerts.find((a) => a?.error)?.error || 'Dependabot') : `${alertCrit} critical · ${alertHigh} high` },
  };
}

const LAYERS = [
  ['runtime', 'Runtime', 'Node version on the plane, every host and every kit vs the floor'],
  ['base', 'Base image', 'node:22-slim digest the fleet builds on, watched daily'],
  ['image', 'Images (SCA)', 'OS + npm packages inside the containers, ECR enhanced scan hourly'],
  ['dast', 'Web (DAST)', 'ZAP, Nuclei, TLS against each public URL, monthly + on demand'],
  ['ci', 'Supply chain (CI)', 'gitleaks, Trivy, runtime floor gating main in both repos'],
  ['deps', 'Dependencies', 'Dependabot alerts on the lockfiles'],
];

const STEP_LABEL = { gitleaks: 'gitleaks', trivyFs: 'Trivy fs', trivyImage: 'Trivy image', runtimeFloor: 'runtime floor' };
const stepState = (s) => (s === 'success' ? 'ok' : s === 'failure' ? 'down' : s === 'skipped' ? 'warn' : 'unknown');

function ImageTop({ top }) {
  if (!top?.length) return <p className="cs-help" style={{ margin: '6px 0 0' }}>No open fixable findings in this image.</p>;
  return (
    <ul className="live-remed" style={{ marginTop: 6 }}>
      {top.map((f) => {
        const fixed = /fixed in ([^ ·]+)/.exec(f.detail || '')?.[1];
        const url = /(https?:\/\/\S+)/.exec(f.detail || '')?.[1];
        return (
          <li key={f.name}>
            <span className={`live-sev-tag is-${f.severity}`}>{f.severity}</span>
            <span className="live-mono">{f.name}</span>
            {fixed ? <span className="cs-help" style={{ margin: 0 }}>fixed in {fixed}</span> : <span className="cs-help" style={{ margin: 0 }}>no fix yet</span>}
            {url ? <a href={url} target="_blank" rel="noreferrer" className="cs-help" style={{ margin: 0 }}>advisory ↗</a> : null}
          </li>
        );
      })}
    </ul>
  );
}

function FindingsExpand({ slug, layer }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let live = true;
    apiGet(`/apps/admin/scans/${slug}`)
      .then((d) => { if (live) setData(d); })
      .catch((e) => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [slug]);
  if (err) return <p className="cs-help">Failed to load findings: {err}</p>;
  if (!data) return <p className="cs-help">Loading findings…</p>;
  const isImage = (f) => f.scanner === 'image';
  const open = (data.findings || []).filter((f) => f.status === 'open' && !f.accepted && (layer === 'image' ? isImage(f) : !isImage(f)));
  const accepted = (data.findings || []).filter((f) => f.accepted && !isImage(f));
  return (
    <div>
      {!open.length && <p className="cs-help">No actionable open {layer === 'image' ? 'image' : 'web'} findings.</p>}
      {open.slice(0, 15).map((f) => (
        <p key={f.id} className="cs-help" style={{ margin: '4px 0' }}>
          <span className={`live-sev-tag is-${f.severity}`}>{f.severity}</span> {f.name} <span className="live-mono">{f.scanner}</span>
          {f.detail ? <span style={{ display: 'block' }}>{String(f.detail).slice(0, 220)}</span> : null}
        </p>
      ))}
      {open.length > 15 ? <p className="cs-help">{open.length - 15} more on the app's Security page.</p> : null}
      {layer !== 'image' && accepted.length ? <p className="cs-help">{accepted.length} accepted as the kit baseline.</p> : null}
    </div>
  );
}

export default function SecurityPosture() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const [openRow, setOpenRow] = useState(null); // `${layer}:${slug}`

  useEffect(() => {
    let alive = true;
    const pull = () => apiGet('/apps/admin/security-posture')
      .then((d) => { if (alive) { setData(d); setErr(''); } })
      .catch((e) => { if (alive) setErr(e.status === 403 ? 'Operator access required.' : e.message); });
    pull();
    const t = setInterval(pull, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const verdicts = useMemo(() => (data ? layerVerdicts(data) : null), [data]);

  const posture = useMemo(() => {
    if (!data || !verdicts) return '';
    const r = data.runtime;
    const bits = [];
    bits.push(verdicts.runtime.state === 'ok' ? `every runtime at the ${r.floor} floor`
      : verdicts.runtime.state === 'down' ? `${r.kits.behind.length + r.hosts.filter((h) => h.behind === true).length + (r.plane.behind === true ? 1 : 0)} runtime(s) below the floor`
        : r.floor ? `${r.kits.unreported.length} kit(s) not reporting Node yet` : 'no runtime floor set');
    bits.push(verdicts.base.state === 'ok' ? 'base image current' : verdicts.base.state === 'warn' ? 'base image moved, release due' : 'base image unresolved');
    bits.push(verdicts.image.state === 'unknown' ? 'image scan not read' : `${verdicts.image.value} in images`);
    bits.push(verdicts.dast.state === 'unknown' ? 'no web baseline yet' : verdicts.dast.value.replace(' to act on', ' with high web findings'));
    bits.push(verdicts.ci.state === 'ok' ? 'CI gates green on both repos' : verdicts.ci.state === 'unknown' ? 'CI not watched' : `CI: ${verdicts.ci.value}`);
    return bits.join(' · ').replace(/^./, (c) => c.toUpperCase());
  }, [data, verdicts]);

  if (err) return <p className="cs-help">{err}</p>;
  if (!data) return <p className="cs-help">Reading the posture…</p>;

  const { runtime, baseImage, images, dast, supplyChain } = data;
  const toggle = (key) => setOpenRow((v) => (v === key ? null : key));

  return (
    <>
      <p className="live-posture" data-nova-note="One-line security posture across six layers; each layer below has its own verdict and evidence">{posture}</p>

      <div className="live-layers" data-nova-note="Defense in depth strip: runtime floor, base image, container images, web DAST, CI gates, Dependabot — one verdict each; the sections below are the evidence">
        {LAYERS.map(([key, label, judge]) => {
          const v = verdicts[key];
          return (
            <a key={key} href={`#posture-${key === 'deps' ? 'ci' : key}`} className={`live-layer is-${v.state}`}>
              <span className="live-layer-head"><Dot state={v.state} /> <strong>{label}</strong></span>
              <span className="live-layer-value">{v.value}</span>
              <span className="cs-help">{v.detail}</span>
              <span className="live-layer-judge">{judge}</span>
            </a>
          );
        })}
      </div>

      <div id="posture-runtime">
        <LiveSection title="Runtime floor" meta={runtime.floor ? `apps.monitor.minNodeVersion ${runtime.floor}` : 'no floor set'}>
          <div data-nova-note="Runtime floor: the plane's own Node, each host's system Node (host agent), each live kit's Node (health phone-home) vs apps.monitor.minNodeVersion; the fleet.runtime-current journey judges the same facts every run">
            <p className="cs-help" style={{ marginTop: 0 }}>
              A Node advisory is answered here: raise the floor, and every runtime below it is named. Hosts and kits report what they run;
              the plane reads its own. Runbook: <Link to="/admin/brain/page/respond-to-a-runtime-advisory">respond-to-a-runtime-advisory</Link>.
            </p>
            <div className="live-rules">
              <div><dt>Control plane</dt><dd><Dot state={runtime.plane.behind === true ? 'down' : runtime.plane.behind === false ? 'ok' : 'unknown'} /> <span className="live-mono">{runtime.plane.node}</span>{runtime.plane.behind === true ? <span className="live-warn"> below floor</span> : ' at floor'}</dd></div>
              <div><dt>Kits</dt><dd><Dot state={runtime.kits.behind.length ? 'down' : runtime.kits.unreported.length ? 'warn' : 'ok'} /> {runtime.kits.atFloor}/{runtime.kits.total} at floor{runtime.kits.behind.length ? <span className="live-warn"> · {runtime.kits.behind.length} behind</span> : ''}{runtime.kits.unreported.length ? <span className="cs-help" style={{ margin: 0 }}> · {runtime.kits.unreported.length} not reporting yet (pre-2026-10-26 kit)</span> : ''}</dd></div>
              {runtime.journey && <div><dt>Journey fleet.runtime-current</dt><dd><Dot state={runtime.journey.ok === true ? 'ok' : runtime.journey.ok === false ? 'down' : 'unknown'} /> {runtime.journey.lastDetail || runtime.journey.lastError || 'not run yet'}{runtime.journey.lastAt ? <span className="cs-help" style={{ margin: 0 }}> · {ago(runtime.journey.lastAt)} · {runtime.journey.passes || 0}/{runtime.journey.runs || 0} passes</span> : null}</dd></div>}
              <div><dt>How it is checked</dt><dd>Kit <span className="live-mono">/health.node</span> and <span className="live-mono">runtime.node</span> → <span className="live-mono">monitor.node</span>; host agent <span className="live-mono">stats.node</span>; CI compares the built image's <span className="live-mono">node --version</span> to the same floor. Trivy does not see the Node binary.</dd></div>
            </div>
            <div className="cs-plan-tablewrap">
              <table>
                <thead><tr><th>Host</th><th>Node</th><th>Floor</th><th>Agent poll</th></tr></thead>
                <tbody>
                  {runtime.hosts.map((h) => (
                    <tr key={h.hostId} className={h.behind === true ? 'is-down' : ''}>
                      <td className="live-mono">{h.hostId}</td>
                      <td className="live-mono">{h.node || '—'}</td>
                      <td>{h.behind === true ? <span className="live-warn">below {h.floor}</span> : h.behind === false ? 'at floor' : 'agent predates runtime facts'}</td>
                      <td>{h.lastPollAt ? ago(h.lastPollAt) : 'never'}</td>
                    </tr>
                  ))}
                  {!runtime.hosts.length && <tr><td colSpan={4} className="cs-help">No active hosts.</td></tr>}
                </tbody>
              </table>
            </div>
            {runtime.kits.behind.length > 0 && (
              <div className="cs-plan-tablewrap" style={{ marginTop: 10 }}>
                <table>
                  <thead><tr><th>Kit below floor</th><th>Node</th><th>Floor</th></tr></thead>
                  <tbody>
                    {runtime.kits.behind.map((k) => (
                      <tr key={k.slug} className="is-down">
                        <td>{k.orgId && k.appId ? <Link to={appLive(k.orgId, k.appId, 'security')}>{k.name}</Link> : k.name} <span className="cs-help">{k.slug}</span></td>
                        <td className="live-mono">{k.node}</td><td className="live-mono">{k.floor}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </LiveSection>
      </div>

      <div id="posture-base">
        <LiveSection title="Base image" meta={baseImage?.ref || 'node:22-slim'}>
          <div data-nova-note="Base-image watch: the node:22-slim digest resolved daily; a move starts the plane UAT build and makes the kit auto-release tag a quiet main; scanner image pins vs what their tags resolve to today">
            <p className="cs-help" style={{ marginTop: 0 }}>
              The base moves under its tag on every Node security release. The watch resolves it daily with one registry HEAD; a move is a release
              reason on its own — the plane rebuilds to UAT and the kit tags a release even if <span className="live-mono">main</span> is quiet, because the
              kit hashes this digest into its build key.
            </p>
            <div className="live-rules">
              <div><dt>Digest</dt><dd><span className="live-mono">{baseImage?.digest ? SHORT(baseImage.digest) : 'unresolved'}</span>{baseImage?.previousDigest ? <span className="cs-help" style={{ margin: 0 }}> · was {SHORT(baseImage.previousDigest)}</span> : null}{baseImage?.error ? <span className="live-warn"> · {baseImage.error}</span> : null}</dd></div>
              <div><dt>Checked</dt><dd>{baseImage?.checkedAt ? ago(baseImage.checkedAt) : 'never'}{baseImage?.changedAt ? ` · last moved ${ago(baseImage.changedAt)}` : ' · no move seen yet'}</dd></div>
              <div><dt>Kit release</dt><dd><Dot state={baseImage?.pendingKitRelease ? 'warn' : 'ok'} /> {baseImage?.pendingKitRelease ? 'due — next auto-release sweep tags main' : 'nothing owed'}</dd></div>
              <div><dt>Plane build</dt><dd><Dot state={baseImage?.pendingPlaneBuild ? 'warn' : 'ok'} /> {baseImage?.pendingPlaneBuild ? `pending${baseImage.planeBuildError ? ` · ${baseImage.planeBuildError}` : ''}` : baseImage?.planeBuild?.at ? `started ${ago(baseImage.planeBuild.at)} (${baseImage.planeBuild.releaseId || 'release'})` : 'nothing owed'}</dd></div>
            </div>
            <div className="cs-plan-tablewrap">
              <table>
                <thead><tr><th>Scanner image</th><th>Pinned</th><th>Tag resolves to</th><th>State</th></tr></thead>
                <tbody>
                  {Object.entries(baseImage?.scanners || {}).map(([name, s]) => (
                    <tr key={name}>
                      <td>{name} <span className="cs-help live-mono">{s.tag}</span></td>
                      <td className="live-mono">{SHORT(s.pinned)}</td>
                      <td className="live-mono">{s.latest ? SHORT(s.latest) : '—'}</td>
                      <td>{s.error ? <span className="live-warn">unreadable · {s.error}</span> : s.current === false ? <span className="live-warn">newer available — bump apps.scan.images</span> : s.current ? 'current' : 'unpinned'}</td>
                    </tr>
                  ))}
                  {!Object.keys(baseImage?.scanners || {}).length && <tr><td colSpan={4} className="cs-help">Scanner pins not resolved yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </LiveSection>
      </div>

      <div id="posture-image">
        <LiveSection title="Container images (SCA)" meta="ECR enhanced scanning · read hourly">
          <div data-nova-note="Container image findings from Amazon Inspector via ECR enhanced scanning: the plane's running image and every distinct kit image instances run; counts are fixable or critical findings only; expand for the top CVEs with fixed-in versions">
            <p className="cs-help" style={{ marginTop: 0 }}>
              What is inside the containers that actually run: OS packages and bundled npm trees. One row per distinct image in service; a CVE is listed
              when it is fixable or critical. Fixes land as a dependency bump or a base-image move and ride the next release.
            </p>
            <div className="cs-plan-tablewrap">
              <table>
                <thead><tr><th>Image</th><th>Runs on</th>{SEV.map((k) => <th key={k}>{k}</th>)}<th>Read</th><th /></tr></thead>
                <tbody>
                  {images.map((img) => {
                    const key = `image:${img.instances[0]}`;
                    return (
                      <React.Fragment key={key}>
                        <tr>
                          <td><span className="live-mono" title={img.image || ''}>{imageShort(img.image)}</span>{img.kitRef ? <span className="cs-help" style={{ display: 'block', margin: 0 }}>{img.kitRef}</span> : <span className="cs-help" style={{ display: 'block', margin: 0 }}>control plane</span>}</td>
                          <td>{img.kind === 'plane' ? 'nventr.io' : `${img.instances.length} instance${img.instances.length === 1 ? '' : 's'}`}</td>
                          <SevCells counts={img.counts} />
                          <td>{img.scanAt ? ago(img.scanAt) : (img.status || 'not yet')}</td>
                          <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => toggle(key)}>{openRow === key ? 'Hide' : 'Top CVEs'}</button></td>
                        </tr>
                        {openRow === key && (
                          <tr><td colSpan={8}>
                            <ImageTop top={img.top} />
                            {img.kind === 'kit' ? <p className="cs-help" style={{ margin: '6px 0 0' }}>Instances: {img.instances.join(', ')}</p> : null}
                          </td></tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </LiveSection>
      </div>

      <div id="posture-dast">
        <LiveSection title="Web (DAST)" meta={dast.nextBaselineAt ? `next monthly baseline ${new Date(dast.nextBaselineAt).toLocaleDateString()}` : ''}>
          <div data-nova-note="Web scanners per hosted instance and for the plane itself: ZAP passive baseline, Nuclei CVE templates (refreshed per job), testssl; high and critical are the ones to act on; accepted counts are the shared kit baseline, not defects">
            <p className="live-accepted">
              High and critical are the ones to act on. Accepted counts are the shared kit baseline (inline scripts the bundle needs, missing subresource
              integrity) and are not an outage. Nuclei templates refresh before every job.
            </p>
            <div className="cs-plan-tablewrap">
              <table>
                <thead><tr><th>App</th><th>Assurance</th><th>Last scan</th>{['critical', 'high', 'medium'].map((k) => <th key={k}>{k}</th>)}<th>Accepted</th><th /></tr></thead>
                <tbody>
                  <tr>
                    <td>nventr.io <span className="cs-help">plane</span><span className="cs-help" style={{ display: 'block', margin: 0 }}>{(dast.plane.targets || []).join(' · ') || 'apps.scan.planeTargets unset'}</span></td>
                    <td>schedule</td>
                    <td>{dast.plane.scanning ? 'scanning' : dast.plane.lastScanAt ? ago(dast.plane.lastScanAt) : 'never'}</td>
                    <SevCells counts={dast.plane.counts} keys={['critical', 'high', 'medium']} />
                    <td className="live-num">{n(dast.plane.counts?.accepted)}</td>
                    <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => toggle('dast:plane')}>{openRow === 'dast:plane' ? 'Hide' : 'Findings'}</button></td>
                  </tr>
                  {openRow === 'dast:plane' && <tr><td colSpan={8}><FindingsExpand slug="plane" layer="dast" /></td></tr>}
                  {dast.instances.map((r) => {
                    const key = `dast:${r.slug}`;
                    return (
                      <React.Fragment key={key}>
                        <tr>
                          <td>{r.orgId && r.appId ? <Link to={appLive(r.orgId, r.appId, 'security')}>{r.name}</Link> : r.name} <span className="cs-help">{r.slug}</span></td>
                          <td>{r.assurance ? 'on' : 'off'}</td>
                          <td>{r.scanning ? 'scanning' : r.lastScanAt ? ago(r.lastScanAt) : 'never'}</td>
                          <SevCells counts={r.counts} keys={['critical', 'high', 'medium']} />
                          <td className="live-num">{n(r.accepted)}</td>
                          <td><button type="button" className="cs-btn cs-btn-sm cs-btn-ghost" onClick={() => toggle(key)}>{openRow === key ? 'Hide' : 'Findings'}</button></td>
                        </tr>
                        {openRow === key && <tr><td colSpan={8}><FindingsExpand slug={r.slug} layer="dast" /></td></tr>}
                      </React.Fragment>
                    );
                  })}
                  {!dast.instances.length && <tr><td colSpan={8} className="cs-help">No live instances.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </LiveSection>
      </div>

      <div id="posture-ci">
        <LiveSection title="Supply chain" meta={supplyChain?.checkedAt ? `GitHub read ${ago(supplyChain.checkedAt)}` : 'not read yet'}>
          <div data-nova-note="GitHub side of the posture: the security CI job on each repo's main (gitleaks, Trivy fs, Trivy image, runtime floor) and open Dependabot alerts; both repos — the App Kit and the control plane">
            <p className="cs-help" style={{ marginTop: 0 }}>
              Both repos gate <span className="live-mono">main</span> with the same job: secrets (gitleaks), lockfile and built-image CVEs (Trivy, CRITICAL fails), and the
              image's Node against the runtime floor. Dependabot files alerts on the lockfiles and opens grouped weekly bumps; majors are migrations, not bumps.
            </p>
            {!supplyChain?.repos || !Object.keys(supplyChain.repos).length ? (
              <p className="cs-help">{supplyChain?.note || 'The supply-chain watch has not run yet (kit-release worker, apps.kitRollout.supplyChainHours).'}</p>
            ) : (
              <div className="live-repos">
                {Object.entries(supplyChain.repos).map(([name, r]) => (
                  <article key={name} className="live-repo">
                    <header>
                      <Dot state={stepState(r.ci?.status === 'pending' ? 'skipped' : r.ci?.status)} />
                      <strong>{name === 'kit' ? 'App Kit' : 'Control plane'}</strong>
                      <span className="cs-help live-mono" style={{ margin: 0 }}>{r.repo}</span>
                    </header>
                    <div className="live-repo-ci">
                      <span>security job on main: <strong>{r.ci?.status || '?'}</strong>{r.ci?.sha ? <span className="live-mono cs-help" style={{ margin: 0 }}> {r.ci.sha.slice(0, 7)}</span> : null}{r.ci?.at ? <span className="cs-help" style={{ margin: 0 }}> · {ago(r.ci.at)}</span> : null}{r.ci?.url ? <> · <a href={r.ci.url} target="_blank" rel="noreferrer">run ↗</a></> : null}{r.ci?.error ? <span className="live-warn"> · {r.ci.error}</span> : null}{r.ci?.note ? <span className="cs-help" style={{ margin: 0 }}> · {r.ci.note}</span> : null}</span>
                      <div className="live-steps">
                        {Object.entries(STEP_LABEL).map(([k, label]) => (
                          <span key={k} className={`live-step is-${stepState(r.ci?.steps?.[k])}`}><Dot state={stepState(r.ci?.steps?.[k])} /> {label}{r.ci?.steps?.[k] && r.ci.steps[k] !== 'success' ? ` · ${r.ci.steps[k]}` : ''}</span>
                        ))}
                      </div>
                    </div>
                    <div className="live-repo-alerts">
                      {r.alerts?.error ? (
                        <span className="cs-help" style={{ margin: 0 }}>Dependabot alerts: {r.alerts.error} — grant the github-token <span className="live-mono">security_events</span> to read them here; the alerts tab on GitHub still has them.</span>
                      ) : (
                        <>
                          <span>Dependabot: <strong>{n(r.alerts?.open)}</strong> open{SEV.map((k) => n(r.alerts?.counts?.[k]) ? <span key={k} className={`live-sev-tag is-${k}`} style={{ marginLeft: 6 }}>{n(r.alerts.counts[k])} {k}</span> : null)}</span>
                          {r.alerts?.top?.length ? (
                            <ul className="live-remed" style={{ marginTop: 6 }}>
                              {r.alerts.top.map((a, i) => (
                                <li key={`${a.package}-${a.cve}-${i}`}>
                                  <span className={`live-sev-tag is-${a.severity}`}>{a.severity}</span>
                                  <span className="live-mono">{a.package}</span>
                                  <span className="cs-help" style={{ margin: 0 }}>{a.cve || ''}{a.manifest ? ` · ${a.manifest}` : ''}{a.fixedIn ? ` · fixed in ${a.fixedIn}` : ''}</span>
                                  {a.url ? <a href={a.url} target="_blank" rel="noreferrer" className="cs-help" style={{ margin: 0 }}>alert ↗</a> : null}
                                </li>
                              ))}
                            </ul>
                          ) : null}
                        </>
                      )}
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </LiveSection>
      </div>
    </>
  );
}
